import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { setHostContext } from '../../redux/features/hostContext/hostContextSlice';
import { HostContext } from '../../embed-script/HostContext';
import i18n from '../../i18n';
import { RootState } from '../../redux/app/store';
import { hostContextStorage } from '../hostContextStorage';

export const useHostContextInitialization = () => {
    const dispatch = useAppDispatch();
    const { isInitialized: hostContextInitialized } = useAppSelector((state: RootState) => state.hostContext);

    const storage = hostContextStorage.autoMode();

    // Helper function to extract host context from URL
    const getHostContextFromUrl = (): HostContext | null => {
        const hostContextParam = new URLSearchParams(window.location.search).get('hostContext');
        if (!hostContextParam) {
            return null;
        }

        // Remove the hostContext parameter from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('hostContext');
        window.history.replaceState({}, '', url.toString());

        // Parse the context data
        try {
            const parsedContext = JSON.parse(hostContextParam) as HostContext;
            console.log('Parsed host context from URL', parsedContext);
            return parsedContext;
        } catch (error) {
            console.error('Failed to parse hostContext from URL', error);
            return null;
        }
    };

    const initializeHostContext = useCallback(() => {
        // Skip if already initialized
        if (hostContextInitialized) {
            return;
        }

        // Helper to update the host context in Redux
        const updateHostContext = (context: HostContext) => {
            dispatch(setHostContext(context));

            // Update language if specified
            if (context.language) {
                void i18n.changeLanguage(context.language);
            }
        };

        // 1. First priority: Check for context in URL
        const hostContextFromUrl = getHostContextFromUrl();

        // 2. Second priority: Check for existing stored context
        const storedHostContext = storage.load();

        // 3. Determine which context to use (URL takes precedence)
        let contextToUse: HostContext = {};

        if (hostContextFromUrl) {
            contextToUse = hostContextFromUrl;
            // Always update storage with URL context
            console.log('Saving host context from URL to storage', hostContextFromUrl);
            storage.save(hostContextFromUrl);
        } else if (storedHostContext) {
            contextToUse = storedHostContext;
            console.log('Using stored host context', storedHostContext);
        }

        // 4. Apply the context
        if (Object.keys(contextToUse).length > 0) {
            console.log('Setting host context', contextToUse);
            updateHostContext(contextToUse);
        } else {
            // Initialize with empty context if nothing else is available
            // Only set id to 'standalone' if we're actually in standalone mode
            const isEmbedded =
                new URLSearchParams(window.location.search).get('embedded') === 'true' || window.self !== window.top;
            const defaultContext: HostContext = isEmbedded ? {} : { id: 'standalone' };
            console.log('Initializing with default context', { isEmbedded, defaultContext });
            updateHostContext(defaultContext);
        }
    }, [dispatch, hostContextInitialized, storage]);

    // Initialize on component mount
    useEffect(() => {
        initializeHostContext();
    }, [initializeHostContext]);
};
